import React from 'react';

import {Anchor, Banner, Icon} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {pluralize} from 'lib/stringUtils';
import moment from 'moment';

const GET_ACCESS_URL =
  'https://help.albert.io/en/articles/4467647-how-do-i-get-access-to-a-school-license';
const PENDING_APPROVAL_URL =
  'https://help.albert.io/en/articles/4333411-what-should-i-do-if-i-am-pending-admin-approval';
const EXPIRING_SOON_DAYS = 60;

// Minimal shapes for the untyped legacy teacher model — only what we read.
interface LicenseInfo {
  get: (key: string) => unknown;
}
interface PersonnelRecord {
  getSchool: () => {getId: () => string; getName: () => string};
}
interface TeacherLicenseModel {
  getSchoolPersonnel: () => {toArray: () => PersonnelRecord[]};
  getMeta: () => {getSchoolLicensesInfo: () => {toArray: () => LicenseInfo[]} | undefined};
}

// Mirrors AccountSettings' InstructionalLicenseCard query so Home reflects the SAME per-school license
// data shown at /account/access. The license data itself comes from the `license_info` META on the
// base teacher resource (admin-readable under masquerade). The `school_personnel_v1` include — only
// used to resolve school NAMES — is dropped while masquerading: an admin can't read OTHER schools'
// personnel and we scope to the masquerade school anyway.
const getTeacherLicenseQuery = () => {
  const query = resource('teachers_v1')
    .mandarkEndpoint(['teachers_v1', masqueradeStore.getUserIdByMasqueradeState()])
    .withMeta('teacher_v1')
    .customQuery({meta: {context: {teacher: {license_info: true}}}});
  return masqueradeStore.isMasquerading()
    ? query
    : query.include('school_personnel_v1').include('school_personnel_v1.school_v5');
};

interface HomeLicenseBanner {
  schoolId: string;
  message: string;
  href: string;
  linkLabel: string;
}

export const LicenseBanners = () => {
  const isMasquerading = masqueradeStore.isMasquerading();
  const masqueradeSchoolId = masqueradeStore.getSchoolId();
  // While masquerading, also await the masquerade school so we can resolve its name (the personnel
  // include that normally supplies names is dropped — see getTeacherLicenseQuery).
  const {isReady} = useAreLatestQueriesReady(
    isMasquerading
      ? [
          () => getTeacherLicenseQuery() as unknown as QueryBuilder,
          () => masqueradeStore.getSchoolQuery() as unknown as QueryBuilder
        ]
      : [() => getTeacherLicenseQuery() as unknown as QueryBuilder]
  );

  if (!isReady) {
    return null;
  }

  const teacher = getTeacherLicenseQuery().getResource() as unknown as TeacherLicenseModel | null;
  // useAreLatestQueriesReady can flip isReady=true on a rejected fetch; guard the model.
  if (!teacher) {
    return null;
  }
  const allLicenseInfos = teacher.getMeta().getSchoolLicensesInfo()?.toArray() ?? [];
  // Under masquerade, surface only the school the admin administers (hide the teacher's other schools).
  const licenseInfos = isMasquerading
    ? allLicenseInfos.filter((info) => String(info.get('school_id')) === masqueradeSchoolId)
    : allLicenseInfos;
  if (!licenseInfos.length) {
    return null;
  }

  // School names: from the personnel include normally; while masquerading the include is dropped, so
  // resolve just the masquerade school's name from the (admin-readable) masquerade store.
  const schoolNamesById = new Map<string, string>();
  if (isMasquerading) {
    const masqueradeSchoolName = (
      masqueradeStore.getSchool() as {getName?: () => string} | null
    )?.getName?.();
    if (masqueradeSchoolId && masqueradeSchoolName) {
      schoolNamesById.set(masqueradeSchoolId, masqueradeSchoolName);
    }
  } else {
    teacher
      .getSchoolPersonnel()
      .toArray()
      .forEach((record) => {
        const school = record.getSchool();
        schoolNamesById.set(school.getId(), school.getName());
      });
  }

  const now = systemTimeOffsetStore.getCurrentTime();
  const banners: HomeLicenseBanner[] = [];

  licenseInfos.forEach((info) => {
    const schoolId = String(info.get('school_id'));
    const schoolName = schoolNamesById.get(schoolId);
    if (!schoolName) {
      return; // no resolvable association (e.g. rejected) — skip
    }

    const hasLicense = info.get('has_school_license') === true;
    const status = info.get('school_license_status'); // 'active' | 'inactive' | null

    if (!hasLicense) {
      banners.push({
        schoolId,
        message: `${schoolName} does not have a license.`,
        href: GET_ACCESS_URL,
        linkLabel: 'Get full access'
      });
      return;
    }
    if (!status) {
      banners.push({
        schoolId,
        message: `You are currently pending approval at ${schoolName}.`,
        href: PENDING_APPROVAL_URL,
        linkLabel: 'Learn more'
      });
      return;
    }
    if (status === 'inactive') {
      banners.push({
        schoolId,
        message: `${schoolName}'s license is inactive.`,
        href: GET_ACCESS_URL,
        linkLabel: 'Learn more'
      });
      return;
    }

    const endDateRaw = info.get('school_license_end_date');
    const endDate = endDateRaw ? moment(endDateRaw as string | number) : null;
    // Only an active, not-yet-expired license that's within the window is "expiring soon".
    if (
      endDate?.isValid() &&
      endDate.isAfter(now) &&
      moment(now).add(EXPIRING_SOON_DAYS, 'days').isAfter(endDate)
    ) {
      const days = endDate.diff(now, 'days');
      const countdown = days === 0 ? 'today' : `in ${days} ${pluralize('day', days)}`;
      banners.push({
        schoolId,
        message: `${schoolName}'s license will expire ${countdown}.`,
        href: GET_ACCESS_URL,
        linkLabel: 'Learn more'
      });
    }
  });

  if (!banners.length) {
    return null;
  }

  return (
    <div className='flex flex-col gap-x1-half'>
      {banners.map((banner) => (
        <Banner alignContent='center' key={banner.schoolId} type='warning'>
          <div className='flex grow items-center gap-x2'>
            <Icon color='inherit' icon='exclamation-triangle' iconStyle='regular' />
            <span className='grow'>{banner.message}</span>
            <Anchor as='a' href={banner.href} rel='noopener noreferrer' target='_blank' underlined>
              {banner.linkLabel}
            </Anchor>
          </div>
        </Banner>
      ))}
    </div>
  );
};
